/**
 * Admin shipping-method write builders — the client-side half of managing a
 * *target seller's* `shipping_methods` rows from the admin listings editor.
 *
 * Why these exist: `shipping_methods` INSERT/UPDATE/DELETE RLS is
 * `user_id = auth.uid()`, so the browser client cannot write another user's
 * methods (the write silently affects 0 rows). When an admin edits another
 * user's listing, `EditShippingTab` routes its writes through these builders
 * instead of the direct supabase calls — each posts to a `requireAdmin()` +
 * `supabaseAdmin` route that bypasses RLS, scoped to the seller in the path.
 *
 * Builders accept *intent* (the editor's `ShippingOption`-shaped fields) and
 * assemble the wire body internally — no JSON assembly leaks into the
 * component (frontend hard-gate).
 *
 * @see packages/contract/src/admin-shipping-method.ts — request/response contract
 * @see app/api/admin/users/[id]/shipping-methods/route.ts — POST handler
 * @see app/api/admin/users/[id]/shipping-methods/[methodId]/route.ts — PUT/DELETE
 */

import type {
  AdminShippingMethodCreate,
  AdminShippingMethodUpdate,
} from "@kraahl/contract";

/** Editor intent for creating/updating a seller shipping method. */
export interface SellerShippingMethodIntent {
  name: string;
  description: string;
  price: string;
  bannedCountries: string[];
  currency: string;
}

function collectionUrl(sellerUserId: string): string {
  return `/api/admin/users/${sellerUserId}/shipping-methods`;
}

function itemUrl(sellerUserId: string, methodId: string): string {
  return `/api/admin/users/${sellerUserId}/shipping-methods/${methodId}`;
}

async function ensureOk(res: Response): Promise<void> {
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(
      `Admin shipping-method write failed: HTTP ${res.status}${
        detail?.error ? ` — ${detail.error}` : ""
      }`,
    );
  }
}

/** Create a shipping method owned by `sellerUserId`. `methodId` is client-minted. */
export async function createSellerShippingMethod(
  sellerUserId: string,
  methodId: string,
  intent: SellerShippingMethodIntent,
): Promise<void> {
  const body: AdminShippingMethodCreate = {
    id: methodId,
    name: intent.name,
    description: intent.description,
    price: intent.price,
    bannedCountries: intent.bannedCountries,
    currency: intent.currency,
  };

  const res = await fetch(collectionUrl(sellerUserId), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await ensureOk(res);
}

/** Update an existing shipping method owned by `sellerUserId`. */
export async function updateSellerShippingMethod(
  sellerUserId: string,
  methodId: string,
  intent: SellerShippingMethodIntent,
): Promise<void> {
  const body: AdminShippingMethodUpdate = {
    name: intent.name,
    description: intent.description,
    price: intent.price,
    bannedCountries: intent.bannedCountries,
    currency: intent.currency,
  };

  const res = await fetch(itemUrl(sellerUserId, methodId), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await ensureOk(res);
}

/** Delete a shipping method owned by `sellerUserId`. */
export async function deleteSellerShippingMethod(
  sellerUserId: string,
  methodId: string,
): Promise<void> {
  const res = await fetch(itemUrl(sellerUserId, methodId), {
    method: "DELETE",
  });
  await ensureOk(res);
}
